// Catálogos revisados de Obras (data/catalogos_obras.json): valores limpios, equivalencias
// de textos antiguos y separación de suelos en proveedor + tipo. El mismo JSON genera el
// script 22_Obras_Catalogos_Revisados.sql del API, así que front y BD usan la misma lista.
import revisados from "./data/catalogos_obras.json";

// Misma comparación que el script SQL (Latin1_General_100_CI_AI): sin mayúsculas,
// tildes ni espacios de más.
export function catalogKey(value) {
    return String(value ?? "")
        .replace(/\s+/g, " ")
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
}

export const REVIEWED_CATALOGS = revisados.catalogos;
const CATALOG_BY_CODE = new Map(REVIEWED_CATALOGS.map((catalog) => [catalog.codigo, catalog]));
const EMPTY_KEYS = new Set(revisados.vacios.map(catalogKey));
// Vacíos de un catálogo concreto (studio: CDS ya no es un estudio -> sin studio).
const EMPTY_KEYS_BY_CATALOG = new Map(REVIEWED_CATALOGS.map((catalog) => [catalog.codigo, new Set((catalog.vacios || []).map(catalogKey))]));

// Catálogos respaldados por dbo.Empresas (proveedores): sus opciones se completan con
// los nombres que ya usan las obras del entorno (no hay tabla de catálogo propia).
export const EMPRESA_CATALOG_CODES = REVIEWED_CATALOGS.filter((catalog) => catalog.origen === "empresas").map((catalog) => catalog.codigo);

// Texto antiguo o variante -> valor limpio, por catálogo.
const CANONICAL_BY_CATALOG = new Map(REVIEWED_CATALOGS.map((catalog) => {
    const map = new Map(catalog.valores.map((value) => [catalogKey(value), value]));
    Object.entries(catalog.equivalencias).forEach(([origin, target]) => map.set(catalogKey(origin), target));
    return [catalog.codigo, map];
}));

const SUELO_BY_KEY = new Map(Object.entries(revisados.suelos.equivalencias).map(([origin, target]) => [catalogKey(origin), target]));

export function reviewedCatalogValues(code) {
    return [...(CATALOG_BY_CODE.get(code)?.valores || [])];
}

// Valor limpio de un texto: equivalencia conocida, vacío para marcadores ("PDTE",
// "FRANQ", "NA"...) y el propio texto (recortado) si no se conoce.
export function canonicalCatalogValue(code, raw) {
    const text = String(raw ?? "").replace(/\s+/g, " ").trim();
    const key = catalogKey(text);
    if (!key || EMPTY_KEYS.has(key) || EMPTY_KEYS_BY_CATALOG.get(code)?.has(key)) return "";
    return CANONICAL_BY_CATALOG.get(code)?.get(key) ?? text;
}

// "MATIMEX - terrazo" -> { proveedor: "Matimex", tipo: "Terrazo" }. Un texto sin
// equivalencia se queda como proveedor (igual que hace el script SQL).
export function splitSueloValue(raw) {
    const text = String(raw ?? "").replace(/\s+/g, " ").trim();
    const key = catalogKey(text);
    if (!key || EMPTY_KEYS.has(key)) return { proveedor: "", tipo: "" };
    const known = SUELO_BY_KEY.get(key);
    if (known) return { proveedor: known.proveedor || "", tipo: known.tipo || "" };
    return { proveedor: canonicalCatalogValue("provSuelo", text), tipo: "" };
}

// Opciones de un catálogo de proveedores, en orden alfabético: las de la lista más los
// nombres que usan las obras del entorno (tal cual están en dbo.Empresas), una por empresa:
// - si solo cambian mayúsculas/tildes ("artena" / "ARTENA") se queda la de la lista;
// - si es otro nombre de la misma empresa ("Grupo Atlante" / "ATLANTE") se queda el que se
//   usa, que es el que existe en ese entorno; si se usan los dos, el de la lista.
export function mergeUsedCatalogValues(code, listValues, usedValues) {
    const result = [];
    const seen = new Set();
    const clean = (value) => String(value ?? "").trim();
    const valid = (text) => text && !EMPTY_KEYS.has(catalogKey(text));
    const canonicalKey = (value) => catalogKey(canonicalCatalogValue(code, value) || value);
    const add = (text) => {
        if (!valid(text) || seen.has(canonicalKey(text))) return;
        seen.add(canonicalKey(text));
        result.push(text);
    };
    const used = usedValues.map(clean).filter(valid);
    const usedKeys = new Set(used.map(catalogKey));
    const list = listValues.map(clean);
    list.filter((value) => usedKeys.has(catalogKey(value))).forEach(add);
    used.forEach(add);
    list.forEach(add);
    return result.sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base", numeric: true }));
}

// Error al renombrar un valor de catálogo ("" si es válido): vacío, largo, igual o repetido
// (sin mayúsculas ni tildes) con otro valor del mismo catálogo. Cambiar solo mayúsculas o
// tildes del propio valor sí se permite ("SPLAU" -> "Splau").
export const CATALOG_VALUE_MAX_LENGTH = 120;
export function catalogRenameError(entries, entry, rawValue) {
    const value = String(rawValue ?? "").replace(/\s+/g, " ").trim();
    if (!value) return "Escribe el nuevo nombre.";
    if (value.length > CATALOG_VALUE_MAX_LENGTH) return `Máximo ${CATALOG_VALUE_MAX_LENGTH} caracteres.`;
    if (value === entry.value) return "Es el mismo nombre.";
    const clash = (entries || []).find((other) => other.id !== entry.id && !other.synthetic && catalogKey(other.value) === catalogKey(value));
    return clash ? `Ya existe «${clash.value}» en este catálogo.` : "";
}

// Aplica mergeUsedCatalogValues a las entradas ({ id, value, parent, active }) de todos
// los catálogos de proveedores. Conserva estado y devuelve el mismo objeto si nada cambia
// (se llama cada vez que cambian las obras).
// `empresas` son las filas de dbo.Empresas que devuelve el API ({ id, value }): dan a cada
// entrada su CodEmpresa, que es lo que permite renombrarla desde la gestión de catálogo.
// `skip`: catálogos que ya vienen completos del API y no se mezclan (studio: empresas de tipo 4).
export function mergeEmpresaCatalogEntries(catalogs, obras, empresas = [], skip = new Set()) {
    let changed = false;
    const next = { ...catalogs };
    const empresaByKey = new Map(empresas.map((empresa) => [catalogKey(empresa.value), empresa]));
    const idOf = (codigo, value, previous) => empresaByKey.get(catalogKey(value))?.id
        ?? previous?.id
        ?? `${codigo}-uso-${catalogKey(value)}`;
    REVIEWED_CATALOGS.filter((catalog) => catalog.origen === "empresas" && !skip.has(catalog.codigo)).forEach((catalog) => {
        const entries = catalogs[catalog.codigo] || [];
        const used = (obras || []).flatMap((obra) => catalog.campos.map((field) => obra?.[field]));
        const values = mergeUsedCatalogValues(catalog.codigo, entries.map((entry) => entry.value), used);
        const byValue = new Map(entries.map((entry) => [entry.value, entry]));
        const merged = values.map((listValue) => {
            // Si la empresa existe en dbo.Empresas se enseña con SU nombre: al guardar, el API
            // la encuentra sin distinguir mayúsculas y la obra vuelve con ese nombre. Con el de
            // la lista ("MALASA" frente a "Malasa") el desplegable ofrecía los dos y elegir
            // uno guardaba el otro.
            const value = empresaByKey.get(catalogKey(listValue))?.value ?? listValue;
            const previous = byValue.get(value) ?? byValue.get(listValue);
            const id = idOf(catalog.codigo, value, previous);
            if (previous) return previous.id === id && previous.value === value ? previous : { ...previous, id, value };
            return { id, value, parent: null, active: true };
        });
        if (merged.length === entries.length && merged.every((entry, index) => entry === entries[index])) return;
        next[catalog.codigo] = merged;
        changed = true;
    });
    return changed ? next : catalogs;
}
